import React from 'react';

import Avatar from 'core/components/Avatar.js';
import BadgeList from 'core/components/BadgeList.js';
import CollapsibleText from 'core/components/CollapsibleText.js';
import Tooltip from 'core/components/Tooltip.js';
import Icon from 'core/components/Icon.js';
import Link from 'core/components/Link.js';
import LinkWithProperties from 'core/components/LinkWithProperties.js';
import {BP_FHIR, ZM, SHOW_REFERRAL_BTN} from 'core/constants.js';
import {TELEHEALTH_ONLY} from 'core/telehealth.js';
import {PracticeStoreContext} from 'core/stores/RootStore.js';
import {
    BPSecureMessageButton,
    BPWriteReferralButton,
    HSReferralButton,
    MDWriteReferralButton,
    ZMWriteReferralButton,
    MultipleReferralsButton,
} from 'professional/components/ReferralButtons.js';
import {sessionStorageGetItem} from 'core/utils.js';
export default class ProfileCard extends React.Component {
    static contextType = PracticeStoreContext;
    static defaultProps = {
        healthFund: {
            id: 0,
        },
    };

    renderSpecialties() {
        const {
            isClient,
            isReferrals,
            isProUser,
        } = this.context.rootStore.paramStore;
        const {specialties} = this.props;
        if (!specialties?.length) {
            return null;
        }
        return (
            <h3>
                {specialties
                    .map((specialty) => {
                        return isClient || isReferrals || isProUser
                            ? specialty.professionalName
                            : specialty.name;
                    })
                    .join(', ')}
            </h3>
        );
    }

    renderSpecialInterests() {
        const {interestsDescription} = this.props;
        if (!interestsDescription) {
            return null;
        }
        return (
            <div>
                <dt>
                    <Icon name="generalPractitioners" />
                </dt>
                <dd>
                    <strong>{'Special interests: '}</strong>
                    <CollapsibleText
                        collapsed={true}
                        maxWords={10}
                        showToggle={false}
                        text={interestsDescription}
                    />
                </dd>
            </div>
        );
    }

    tooltipContent(text) {
        return <p>{text}</p>;
    }

    renderLocation() {
        const {isPracticeGroup} = this.context;
        const {address, locations} = this.props;
        if (!address && !locations?.length) {
            return null;
        }
        let displayElmt = null;
        if (isPracticeGroup && locations?.length > 0) {
            const locString = locations.reduce(function (acc, location, idx) {
                const locName = `${location.locality} ${location.state}`;
                if (acc.includes(locName)) {
                    return acc;
                } else {
                    return `${acc}${idx === 0 ? '' : ', '}${locName}`;
                }
            }, '');
            let stateStr = '';
            if (locations.length < 5) {
                displayElmt = <address>{locString}</address>;
            } else {
                stateStr = locations.reduce(function (acc, location, idx) {
                    if (acc.includes(location.state)) {
                        return acc;
                    } else {
                        return `${acc}${idx === 0 ? '' : ', '}${
                            location.state
                        }`;
                    }
                }, `${locations.length} practices across `);
                displayElmt = (
                    <Tooltip
                        content={this.tooltipContent(locString)}
                        defaultPosition="top"
                        timeOut={200}
                    >
                        <address>{stateStr}</address>
                    </Tooltip>
                );
            }
        } else if (address) {
            displayElmt = <address>{address}</address>;
        }
        return (
            <div>
                <dt>
                    <Icon name="mapPin" />
                    {'Address: '}
                </dt>
                <dd>{displayElmt}</dd>
            </div>
        );
    }

    renderFees() {
        const {hasFees, healthFund, id, profileUrl, specialties} = this.props;
        const hasMedicalSpecialty = specialties?.find((sp) => sp.isMedical);
        const iconColor = healthFund?.colorBrand
            ? {color: `#${healthFund.colorBrand}`}
            : undefined;
        if (hasFees && healthFund?.id && hasMedicalSpecialty) {
            return (
                <div>
                    <dt className="fees-upsell" style={iconColor}>
                        <Icon name="feesGraph" />
                        {'Out-of-pocket-cost: '}
                    </dt>
                    <dd>
                        <LinkWithProperties
                            hashParams={'fees'}
                            href={profileUrl}
                            searchParams={
                                healthFund?.id > 0
                                    ? {'health_fund': healthFund.id}
                                    : undefined
                            }
                        >
                            {'See out-of-pocket costs'}
                        </LinkWithProperties>
                    </dd>
                </div>
            );
        }

        return null;
    }

    getReferralData(data, type) {
        const {name, offersTelehealth} = this.context.practice;
        const referralData = {...data};
        if (offersTelehealth === TELEHEALTH_ONLY) {
            // remove address data if pure telehealth
            if (type == ZM) {
                referralData.PracticeAddress = 'Telehealth';
                return referralData;
            }
            // bp data
            delete referralData.AddressLine1;
            delete referralData.AddressLine2;
            delete referralData.Postcode;
            referralData.Suburb = `${name} - Telehealth`;

            //md data
            delete referralData.City;
            delete referralData.PCity;
            delete referralData.PPostcode;
            delete referralData.PStreetLine1;
            delete referralData.PStreetLine2;
            delete referralData.PStreetLine3;
            delete referralData.StreetLine1;
            delete referralData.StreetLine2;
            delete referralData.StreetLine3;
        }
        return referralData;
    }

    renderReferralButtons(
        bpData,
        mdData,
        zmData,
        allowPractitionerReferral,
        location,
    ) {
        if (!allowPractitionerReferral) {
            return null;
        }
        const {getCopyData} = this.context;
        const {client, isReferrals} = this.context.rootStore.paramStore;
        const {displayName, id} = this.props;
        const showReferralBtn =
            sessionStorageGetItem(SHOW_REFERRAL_BTN) === 'true';
        if (bpData || mdData || (zmData && showReferralBtn)) {
            return (
                <>
                    {bpData && (
                        <BPWriteReferralButton
                            bpData={this.getReferralData(bpData)}
                            eventData={{
                                practicePositionId: id,
                            }}
                        />
                    )}
                    {mdData && (
                        <MDWriteReferralButton
                            eventData={{
                                practicePositionId: id,
                            }}
                            mdData={this.getReferralData(mdData)}
                        />
                    )}
                    {bpData && client === BP_FHIR && (
                        <BPSecureMessageButton
                            eventData={{
                                practicePositionId: id,
                            }}
                            fhirData={this.getReferralData(bpData)}
                        />
                    )}
                    {zmData && showReferralBtn && (
                        <ZMWriteReferralButton
                            eventData={{practicePositionId: id}}
                            zmData={this.getReferralData(zmData, ZM)}
                        />
                    )}
                </>
            );
        } else if ((zmData && !showReferralBtn) || isReferrals) {
            const {referralEmail} = this.props;
            const getCopyDataWithProfile = () => {
                return getCopyData(displayName, {...location, referralEmail});
            };
            return (
                <HSReferralButton
                    eventData={{
                        eventName: 'copyPracticeDetails',
                        data: {
                            'practice_position': id,
                        },
                    }}
                    getCopyData={getCopyDataWithProfile}
                />
            );
        }
        return null;
    }

    renderPracticeGroupReferralButtons() {
        const {clientData} = this.props;
        const practiceLocations = this.getPractitionerLocationsInGroup();
        const locAllowsPractitionerReferral = practiceLocations?.find(
            (loc) => loc.referralToPractitioner,
        );

        if (!locAllowsPractitionerReferral) {
            return null;
        }

        if (practiceLocations.length === 1) {
            const bpData = clientData[0].bpData;
            const mdData = clientData[0].mdData;
            const zmData = clientData[0].zmData;
            return this.renderReferralButtons(
                bpData,
                mdData,
                zmData,
                true,
                practiceLocations[0],
            );
        }

        if (practiceLocations.length > 1) {
            const {displayName, id} = this.props;
            const {
                showModal,
                rootStore: {
                    paramStore: {client, isReferrals},
                },
            } = this.context;

            return (
                <MultipleReferralsButton
                    client={client}
                    clientData={clientData}
                    displayName={displayName}
                    id={id}
                    isReferrals={isReferrals}
                    locations={practiceLocations}
                    showModal={showModal}
                />
            );
        }

        return null;
    }
    getPractitionerLocationsInGroup() {
        const {
            practice: {locations},
        } = this.context;
        const {displayName} = this.props;
        const locationsInGroup = locations?.filter((location) => {
            const practitioners = location.practitioners || [];
            const locationHasPracititoner = practitioners?.find(
                (practitioner) => practitioner.name === displayName,
            );
            if (locationHasPracititoner && location.referralToPractitioner) {
                return location;
            }
        });
        return locationsInGroup;
    }

    render() {
        const {
            avatar,
            bpData,
            displayName,
            enhanced,
            healthFund,
            id,
            initials,
            mdData,
            offersTelehealth,
            profileUrl,
            referralToPractitioner,
            zmData,
        } = this.props;
        const {
            isPracticeGroup,
            rootStore: {
                paramStore: {isStaff},
            },
        } = this.context;

        return (
            <li className="cell">
                {profileUrl && (
                    <LinkWithProperties
                        customClass="card-link"
                        href={profileUrl}
                        label={`${displayName} profile link`}
                        searchParams={
                            healthFund?.id > 0
                                ? {'health_fund': healthFund.id}
                                : undefined
                        }
                    ></LinkWithProperties>
                )}
                <Avatar
                    avatar={avatar}
                    displayName={displayName}
                    initials={initials}
                    profileId={id}
                />
                <h2>
                    {displayName}{' '}
                    {isStaff && enhanced && <Icon name="checkmark" />}
                    <Link href={profileUrl}>
                        {'Show profile'}
                        <Icon name="arrow" />
                    </Link>
                </h2>
                {this.renderSpecialties()}
                <dl className="icon-list">
                    {this.renderLocation()}
                    {this.renderSpecialInterests()}
                    {this.renderFees()}
                    <BadgeList offersTelehealth={offersTelehealth} />
                    {isPracticeGroup
                        ? this.renderPracticeGroupReferralButtons()
                        : this.renderReferralButtons(
                              bpData,
                              mdData,
                              zmData,
                              referralToPractitioner,
                          )}
                </dl>
            </li>
        );
    }
}
